import * as React from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import {
  getMedicationDosageLabelFromTypeId,
  getMedicationFrequencyLabelFromTypeId,
} from '@app/domain/patient-medication-history/patient-medication-history.helpers'
import type {
  PatientMedicationRecord,
  PatientMedication,
  PatientMedicationRecordEntry,
  MedicationDosageUnit,
  MedicationFrequencyUnit} from '@app/domain/patient-medication-history/patient-medication-history.interface';
import {
  PatientMedicationRecordEntryStatus,
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { ActionLink } from '@app/utils/styles/action-link'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  addMedicationRecordEntry: (
    patientMedication: PatientMedication,
    medicationRecord: PatientMedicationRecord,
  ) => void
  editMedicationEntry: (
    patientMedication: PatientMedication,
    medicationRecord: PatientMedicationRecord,
  ) => void
  medicationRecord: PatientMedicationRecord
  medicationRecordEntries: PatientMedicationRecordEntry[]
  medicationDosageUnits: MedicationDosageUnit[]
  medicationFrequencyUnits: MedicationFrequencyUnit[]
  patientMedication: PatientMedication
}

const RecordEntry = styled.div`
  margin: 'auto';
`

export const MedicationRecordEntry = ({
  addMedicationRecordEntry,
  editMedicationEntry,
  medicationRecord,
  medicationRecordEntries,
  medicationDosageUnits,
  medicationFrequencyUnits,
  patientMedication,
}: Props) => {
  const handleAddMedicationRecordEntry = useCallback(() => {
    addMedicationRecordEntry(patientMedication, medicationRecord)
  }, [addMedicationRecordEntry, medicationRecord, patientMedication])

  const handleEditMedicationRecordEntry = useCallback(() => {
    editMedicationEntry(patientMedication, medicationRecord)
  }, [editMedicationEntry, medicationRecord, patientMedication])

  const currentRecordEntry = useMemo(() => {
    const record = medicationRecordEntries.filter(
      (e) =>
        e.patientMedicationId === patientMedication.id &&
        e.patientMedicationRecordId === medicationRecord.id,
    )[0]

    if (record) {
      if (record.status === PatientMedicationRecordEntryStatus.Discontinued) {
        return (
          <RecordEntry>
            <ActionLink onClick={handleEditMedicationRecordEntry}>
              Discontinued
            </ActionLink>
          </RecordEntry>
        )
      }

      return (
        <RecordEntry>
          <ActionLink onClick={handleEditMedicationRecordEntry}>
            {record.dosage +
              ' ' +
              getMedicationDosageLabelFromTypeId(
                patientMedication.medicationDosageUnitId,
                medicationDosageUnits,
              )}
            <br />
            {record.frequency +
              ' ' +
              getMedicationFrequencyLabelFromTypeId(
                record.frequencyUnitId,
                medicationFrequencyUnits,
              )}
          </ActionLink>
        </RecordEntry>
      )
    } else {
      return (
        <RecordEntry>
          <SmallButton
            variant="primary"
            onClick={handleAddMedicationRecordEntry}
          >
            Edit
          </SmallButton>
        </RecordEntry>
      )
    }
  }, [
    handleAddMedicationRecordEntry,
    handleEditMedicationRecordEntry,
    medicationDosageUnits,
    medicationFrequencyUnits,
    medicationRecord.id,
    medicationRecordEntries,
    patientMedication.id,
    patientMedication.medicationDosageUnitId,
  ])

  return currentRecordEntry
}
